import { DefinedUseQueryResult, useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import axios, { AxiosError } from "axios"
import { API_URL, IApiResponse, IApiResponseObject, IGlobalError } from "@/shared"
import { showNotification } from "@mantine/notifications"
import { AssignStopsResult, AvailableItemsResponse, DailySchedule, DeliveryStop, DeliveryStopItem, DeliveryStopOdooSyncResult, DeliveryStopProducto, PendingProductGroup, QueryDeliveryStopParams, SaveDeliveryStopDto } from "../domain/delivery-stop.types"

export const DELIVERY_STOP_KEYS = {
  FIND_PAGINATED: "DELIVERY_STOP_FIND_PAGINATED",
  DAILY_SCHEDULE: "DELIVERY_STOP_DAILY_SCHEDULE",
  AVAILABLE_ITEMS: "DELIVERY_STOP_AVAILABLE_ITEMS",
  PENDING_PRODUCTS: "DELIVERY_STOP_PENDING_PRODUCTS"
}

export interface OdooStatusResponse {
  connected: boolean
  url: string
  database: string
}

export const useOdooStatus = (): UseQueryResult<OdooStatusResponse | undefined, AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: ["DELIVERY_STOP_ODOO_STATUS"],
    queryFn: async () => {
      const res = await axios.get<IApiResponseObject<OdooStatusResponse>>(`${API_URL}/logistics/delivery-stops/odoo-status`)
      return res.data.data
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true
  })
}

export interface SupabaseStatusResponse {
  connected: boolean
  url: string
}

export const useSupabaseStatus = (): UseQueryResult<SupabaseStatusResponse | undefined, AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: ["DELIVERY_STOP_SUPABASE_STATUS"],
    queryFn: async () => {
      const res = await axios.get<IApiResponseObject<SupabaseStatusResponse>>(`${API_URL}/logistics/delivery-stops/supabase-status`)
      return res.data.data
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true
  })
}

export const useDeliveryStops = (params: QueryDeliveryStopParams): DefinedUseQueryResult<IApiResponse<DeliveryStop>, AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED, params],
    queryFn: async () => {
      const res = await axios.get<IApiResponse<DeliveryStop>>(`${API_URL}/logistics/delivery-stops`, { params })
      return res.data
    },
    initialData: { data: [], totalRecords: 0 },
    refetchOnWindowFocus: false
  })
}

export const useUpdateDeliveryStop = (): UseMutationResult<DeliveryStop, AxiosError<IGlobalError>, { id: string; stopData: SaveDeliveryStopDto }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, stopData }) => {
      const res = await axios.patch<DeliveryStop>(`${API_URL}/logistics/delivery-stops/${id}`, stopData)
      return res.data
    },
    onSuccess: () => {
      showNotification({ title: "Punto de entrega actualizado", message: "El punto de entrega ha sido actualizado exitosamente.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.AVAILABLE_ITEMS] })
    },
    onError: (error) => {
      showNotification({ title: "Error al actualizar punto de entrega", message: error.response?.data?.message || "No se pudo actualizar el punto de entrega. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useCompleteDeliveryStop = (): UseMutationResult<IApiResponseObject<DeliveryStop>, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<DeliveryStop>>(`${API_URL}/logistics/delivery-stops/${id}/complete`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Punto de entrega completado", message: data?.message || "El punto de entrega ha sido marcado como completado.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al completar punto de entrega", message: error.response?.data?.message || "No se pudo marcar el punto de entrega como completado. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useDeleteDeliveryStop = (): UseMutationResult<{ message: string }, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.delete<{ message: string }>(`${API_URL}/logistics/delivery-stops/${id}`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Punto de entrega eliminado", message: data?.message || "El punto de entrega ha sido eliminado.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al eliminar punto de entrega", message: error.response?.data?.message || "No se pudo eliminar el punto de entrega. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useCancelDeliveryStop = (): UseMutationResult<IApiResponseObject<DeliveryStop>, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<DeliveryStop>>(`${API_URL}/logistics/delivery-stops/${id}/cancel`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Punto de entrega cancelado", message: data?.message || "El punto de entrega ha sido cancelado.", color: "orange" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al cancelar punto de entrega", message: error.response?.data?.message || "No se pudo cancelar el punto de entrega. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useBulkAssignRider = (): UseMutationResult<IApiResponseObject<{ assigned: number }>, AxiosError<IGlobalError>, { riderId: string; scheduledDate: string }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto) => {
      const res = await axios.patch<IApiResponseObject<{ assigned: number }>>(`${API_URL}/logistics/delivery-stops/bulk-assign`, dto)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Repartidor asignado", message: data?.message ?? "Puntos de entrega asignados exitosamente.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al asignar repartidor", message: error.response?.data?.message || "No se pudo asignar el repartidor.", color: "red" })
    }
  })
}

const DAILY_SCHEDULE_REFRESH_MS = 30_000

/**
 * Tablero de programación diaria: despachadores con sus entregas y recojos mezclados, y lo que sigue sin asignar.
 * `country` (solo el admin): el tablero de ese país; sin él, los dos (al que no es admin el back le da el suyo).
 */
export const useDailySchedule = (date: string, country?: string): UseQueryResult<DailySchedule | undefined, AxiosError<IGlobalError>> => {
  return useQuery({
    // Empieza con FIND_PAGINATED a propósito: toda mutación de puntos ya invalida esa llave, y así el tablero se refresca solo
    queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED, DELIVERY_STOP_KEYS.DAILY_SCHEDULE, date, country ?? ""],
    queryFn: async () => {
      const res = await axios.get<IApiResponseObject<DailySchedule>>(`${API_URL}/logistics/delivery-stops/daily-schedule`, { params: { date, country } })
      return res.data.data
    },
    refetchInterval: DAILY_SCHEDULE_REFRESH_MS,
    refetchOnWindowFocus: true
  })
}

export const useAssignStops = (): UseMutationResult<IApiResponseObject<AssignStopsResult>, AxiosError<IGlobalError>, { stopIds: string[]; riderId: string }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto) => {
      const res = await axios.patch<IApiResponseObject<AssignStopsResult>>(`${API_URL}/logistics/delivery-stops/assign-stops`, dto)
      return res.data
    },
    onSuccess: (data) => {
      const skipped = data?.data?.skipped ?? []
      showNotification({
        title: skipped.length ? "Asignado con omisiones" : "Despachador asignado",
        message: skipped.length ? `${data?.message ?? ""}: ${skipped.map((s) => s.reason).join("; ")}` : (data?.message ?? "Puntos asignados."),
        color: skipped.length ? "orange" : "green"
      })
    },
    onError: (error) => {
      showNotification({ title: "No se pudo asignar", message: error.response?.data?.message || "Inténtalo nuevamente.", color: "red" })
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    }
  })
}

export const useSyncDeliveryStopsFromOdoo = (): UseMutationResult<IApiResponseObject<DeliveryStopOdooSyncResult>, AxiosError<IGlobalError>, { dateFrom?: string; dateTo?: string } | void> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (params) => {
      const res = await axios.post<IApiResponseObject<DeliveryStopOdooSyncResult>>(`${API_URL}/logistics/delivery-stops/sync-odoo`, null, { params })
      return res.data
    },
    onSuccess: (data) => {
      const r = data?.data
      const msg = r ? `${r.agregadas ?? 0} puntos de entrega nuevos de ${r.total ?? 0} órdenes (${r.fechaDesde} — ${r.fechaHasta})` : data?.message ?? "Sync completado."
      showNotification({ title: "Sync Odoo completado", message: msg, color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error en sync Odoo", message: error.response?.data?.message || "No se pudo sincronizar desde Odoo. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export interface MergeStopAssignment {
  stopId: string
  items: DeliveryStopItem[]
}

export const useMergeStops = (): UseMutationResult<IApiResponseObject<{ updated: number; emptied: number }>, AxiosError<IGlobalError>, { assignments: MergeStopAssignment[] }> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ assignments }) => {
      const res = await axios.patch<IApiResponseObject<{ updated: number; emptied: number }>>(`${API_URL}/logistics/delivery-stops/merge`, { assignments })
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Puntos fusionados", message: data?.message || "Puntos de entrega actualizados exitosamente.", color: "green" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al fusionar puntos", message: error.response?.data?.message || "No se pudo fusionar los puntos de entrega seleccionados.", color: "red" })
    }
  })
}

export const useAvailableItems = (): UseQueryResult<AvailableItemsResponse, AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: [DELIVERY_STOP_KEYS.AVAILABLE_ITEMS],
    queryFn: async () => {
      const res = await axios.get<AvailableItemsResponse>(`${API_URL}/logistics/delivery-stops/available-items`)
      return res.data
    },
    refetchOnWindowFocus: false
  })
}

export const useConfirmDeliveryStop = (): UseMutationResult<IApiResponseObject<DeliveryStop & { odooSent: boolean; appSent: boolean }>, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<DeliveryStop & { odooSent: boolean; appSent: boolean }>>(`${API_URL}/logistics/delivery-stops/${id}/confirm`)
      return res.data
    },
    onSuccess: (data) => {
      const odooSent = data?.data?.odooSent ?? true
      const appSent = data?.data?.appSent ?? true
      const color = odooSent && appSent ? "teal" : odooSent || appSent ? "orange" : "red"
      showNotification({ title: "Punto de entrega confirmado", message: data?.message || "El punto de entrega ha sido confirmado y sincronizado.", color })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.AVAILABLE_ITEMS] })
    },
    onError: (error) => {
      showNotification({ title: "Error al confirmar punto de entrega", message: error.response?.data?.message || "No se pudo confirmar el punto de entrega. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useConfirmarRecojo = (): UseMutationResult<IApiResponseObject<DeliveryStop & { disetSent: boolean }>, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<DeliveryStop & { disetSent: boolean }>>(`${API_URL}/logistics/delivery-stops/${id}/confirmar-recojo`)
      return res.data
    },
    onSuccess: (data) => {
      const disetSent = data?.data?.disetSent ?? true
      showNotification({ title: "Punto de recojo confirmado", message: data?.message || "El punto de recojo ha sido confirmado y sincronizado.", color: disetSent ? "teal" : "orange" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.AVAILABLE_ITEMS] })
    },
    onError: (error) => {
      showNotification({ title: "Error al confirmar punto de recojo", message: error.response?.data?.message || "No se pudo confirmar el punto de recojo. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const usePreviewDisetPayload = (): UseMutationResult<IApiResponseObject<object | null>, AxiosError<IGlobalError>, string> => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.get<IApiResponseObject<object | null>>(`${API_URL}/logistics/delivery-stops/${id}/diset-preview`)
      return res.data
    },
    onError: (error) => {
      showNotification({ title: "Error al previsualizar", message: error.response?.data?.message || "No se pudo armar la previsualización del envío a Odoo.", color: "red" })
    }
  })
}

export const usePreviewAppPayload = (): UseMutationResult<IApiResponseObject<object>, AxiosError<IGlobalError>, string> => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.get<IApiResponseObject<object>>(`${API_URL}/logistics/delivery-stops/${id}/app-preview`)
      return res.data
    },
    onError: (error) => {
      showNotification({ title: "Error al previsualizar", message: error.response?.data?.message || "No se pudo armar la previsualización del envío a la app móvil.", color: "red" })
    }
  })
}

export const useResendToDiset = (): UseMutationResult<IApiResponseObject<{ sent: boolean }>, AxiosError<IGlobalError>, string> => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<{ sent: boolean }>>(`${API_URL}/logistics/delivery-stops/${id}/resend-diset`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Reenviado a Odoo", message: data?.message || "El punto de entrega fue reenviado a Odoo.", color: "teal" })
    },
    onError: (error) => {
      showNotification({ title: "Error al reenviar a Odoo", message: error.response?.data?.message || "No se pudo reenviar el punto de entrega a Odoo. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useResendToApp = (): UseMutationResult<IApiResponseObject<{ sent: boolean }>, AxiosError<IGlobalError>, string> => {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<{ sent: boolean }>>(`${API_URL}/logistics/delivery-stops/${id}/resend-app`)
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Reenviado a la app", message: data?.message || "El punto de entrega fue reenviado a la app móvil.", color: "teal" })
    },
    onError: (error) => {
      showNotification({ title: "Error al reenviar a la app", message: error.response?.data?.message || "No se pudo reenviar el punto de entrega a la app móvil. Inténtelo nuevamente.", color: "red" })
    }
  })
}

/** Limpia el aviso de "productos nuevos" de una parada — se llama al abrir su detalle desde la tabla */
export const useClearNewProductsCount = (): UseMutationResult<IApiResponseObject<null>, AxiosError<IGlobalError>, string> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await axios.patch<IApiResponseObject<null>>(`${API_URL}/logistics/delivery-stops/${id}/clear-new-products`)
      return res.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    }
  })
}

/** Bolsa de productos nuevos que llegaron para una SO cuyo punto de entrega ya estaba confirmado/completado, a la espera de decisión */
export const usePendingProductGroups = (): UseQueryResult<PendingProductGroup[], AxiosError<IGlobalError>> => {
  return useQuery({
    queryKey: [DELIVERY_STOP_KEYS.PENDING_PRODUCTS],
    queryFn: async () => {
      const res = await axios.get<IApiResponseObject<PendingProductGroup[]>>(`${API_URL}/logistics/pending-products`)
      return res.data.data ?? []
    },
    refetchInterval: 60000,
    refetchOnWindowFocus: true
  })
}

export const useDecidePendingGroup = (): UseMutationResult<
  IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>,
  AxiosError<IGlobalError>,
  { id: string; decision: "include" | "defer" }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, decision }) => {
      const res = await axios.patch<IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>>(`${API_URL}/logistics/pending-products/${id}/decide`, { decision })
      return res.data
    },
    onSuccess: (data, { decision }) => {
      const odooSent = data?.data?.odooSent ?? true
      const appSent = data?.data?.appSent ?? true
      const color = decision === "defer" ? "gray" : odooSent && appSent ? "teal" : odooSent || appSent ? "orange" : "red"
      showNotification({ title: decision === "include" ? "Productos incluidos" : "Grupo dejado en espera", message: data?.message || "Listo.", color })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.PENDING_PRODUCTS] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al decidir", message: error.response?.data?.message || "No se pudo procesar la decisión. Inténtelo nuevamente.", color: "red" })
    }
  })
}

/** Excluye productos de una SO en un punto (checklist en "Editar punto de entrega") — quedan en la bolsa de pendientes */
export const useExcludeProductsFromItem = (): UseMutationResult<
  IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>,
  AxiosError<IGlobalError>,
  { id: string; soId: number; productos: DeliveryStopProducto[]; motivo?: string }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, soId, productos, motivo }) => {
      const res = await axios.patch<IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>>(`${API_URL}/logistics/delivery-stops/${id}/exclude-products`, {
        soId,
        productos,
        motivo
      })
      return res.data
    },
    onSuccess: (data) => {
      showNotification({ title: "Productos excluidos", message: data?.message || "Los productos fueron excluidos del punto y quedaron en la bolsa de pendientes.", color: "teal" })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.PENDING_PRODUCTS] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al excluir productos", message: error.response?.data?.message || "No se pudo excluir los productos del punto. Inténtelo nuevamente.", color: "red" })
    }
  })
}

export const useAssignPendingGroup = (): UseMutationResult<
  IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>,
  AxiosError<IGlobalError>,
  { id: string; stopId: string }
> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, stopId }) => {
      const res = await axios.patch<IApiResponseObject<(DeliveryStop & { odooSent: boolean; appSent: boolean }) | null>>(`${API_URL}/logistics/pending-products/${id}/assign`, { stopId })
      return res.data
    },
    onSuccess: (data) => {
      const odooSent = data?.data?.odooSent ?? true
      const appSent = data?.data?.appSent ?? true
      const color = odooSent && appSent ? "teal" : odooSent || appSent ? "orange" : "red"
      showNotification({ title: "Productos asignados", message: data?.message || "Los productos fueron asignados al punto de entrega.", color })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.PENDING_PRODUCTS] })
      void queryClient.invalidateQueries({ queryKey: [DELIVERY_STOP_KEYS.FIND_PAGINATED] })
    },
    onError: (error) => {
      showNotification({ title: "Error al asignar", message: error.response?.data?.message || "No se pudo asignar los productos al punto de entrega. Inténtelo nuevamente.", color: "red" })
    }
  })
}

